import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Pencil, Plus, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import type { AdminProductSummary, CatalogUnit } from "@/lib/types";
import { fetchAdminProducts } from "@/lib/adminProductsApi";
import { CostComponentHistory } from "@/components/admin/CostComponentHistory";
import { isOperationalChange, isRawChange, type CostHistoryView } from "@/lib/costHistoryFormat";
import {
  fetchPackagingMaterialHistory,
  type PackagingMaterialCostHistoryEntry,
} from "@/lib/adminPackagingMaterialsApi";
import {
  INCOMPLETE_REASON_LABELS,
  PACKAGING_REASON_LABELS,
  clearProductPackaging,
  fetchPackagingMaterialOptions,
  fetchProductCost,
  fetchProductCostHistory,
  fetchProductPackagingHistory,
  updateProductCost,
  updateProductCostOrigin,
  upsertProductPackaging,
  type OperationalCostMode,
  type PackagingCompositionState,
  type PackagingMaterialOption,
  type PackagingPresentation,
  type ProductCostDetail,
  type ProductCostHistoryEntry,
  type ProductPackagingHistoryEntry,
} from "@/lib/adminProductCostsApi";

const toRawView = (h: ProductCostHistoryEntry): CostHistoryView => ({
  id: h.id, changedAt: h.changedAt, changedBy: h.changedBy, origin: h.origin,
  oldValues: h.oldValues, newValues: h.newValues, unitName: h.rawCostUnitName, note: h.note,
  importId: h.importId, costApplicationId: h.costApplicationId,
  nfSupplierName: h.nfSupplierName, nfAccessKey: h.nfAccessKey, nfNumber: h.nfNumber,
});
const toOperationalView = (h: ProductCostHistoryEntry): CostHistoryView => ({
  id: h.id, changedAt: h.changedAt, changedBy: h.changedBy, origin: h.origin,
  oldValues: h.oldValues, newValues: h.newValues, unitName: h.operationalUnitName, note: h.note,
  importId: h.importId, costApplicationId: h.costApplicationId,
  nfSupplierName: h.nfSupplierName, nfAccessKey: h.nfAccessKey, nfNumber: h.nfNumber,
});
// Custo de um material de embalagem: tem origem própria (MANUAL/XLSX/NF) e referência.
const toMaterialView = (h: PackagingMaterialCostHistoryEntry): CostHistoryView => ({
  id: h.id, changedAt: h.changedAt, changedBy: h.changedBy, origin: h.origin,
  oldValues: h.oldValues, newValues: h.newValues, unitName: h.materialUnit, note: h.note,
  importId: h.importId, costApplicationId: h.costApplicationId,
  nfSupplierName: h.nfSupplierName, nfAccessKey: h.nfAccessKey, nfNumber: h.nfNumber,
});
// Composição de embalagem (material + quantidade): alteração sempre manual (sem origem XLSX/NF).
const toCompositionView = (h: ProductPackagingHistoryEntry): CostHistoryView => ({
  id: h.id, changedAt: h.changedAt, changedBy: h.changedBy, origin: "MANUAL",
  oldValues: h.oldValues, newValues: h.newValues, note: h.note,
});

const brl = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });
const formatBRL = (value: number | null | undefined) => (value == null ? "—" : brl.format(value));
const formatPercent = (value: number | null | undefined) =>
  value == null ? "—" : `${Number.isInteger(value) ? value : value.toFixed(2).replace(/\.?0+$/, "")}%`;
const formatDate = (iso: string | null | undefined) =>
  iso ? new Date(`${iso}T00:00:00`).toLocaleDateString("pt-BR") : "—";
const parseDecimal = (raw: string): number | null => {
  const t = raw.trim();
  if (t === "") return null;
  const n = Number(t.replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

interface FormState {
  rawCostPrice: string;
  rawCostUnitId: string;
  rawCostReferenceDate: string;
  rawCostNote: string;
  operationalMode: "" | OperationalCostMode;
  operationalValue: string;
  operationalPercent: string;
  operationalUnitId: string;
  operationalNote: string;
}

const NONE = "__none__";

function toForm(detail: ProductCostDetail): FormState {
  return {
    rawCostPrice: detail.rawCostPrice != null ? String(detail.rawCostPrice) : "",
    rawCostUnitId: detail.rawCostUnitId ?? "",
    rawCostReferenceDate: detail.rawCostReferenceDate ?? "",
    rawCostNote: detail.rawCostNote ?? "",
    operationalMode: detail.operationalMode ?? "",
    operationalValue: detail.operationalValue != null ? String(detail.operationalValue) : "",
    operationalPercent: detail.operationalPercent != null ? String(detail.operationalPercent) : "",
    operationalUnitId: detail.operationalUnitId ?? "",
    operationalNote: detail.operationalNote ?? "",
  };
}

export function ProductCostPanel({
  productId,
  canEdit,
  units,
  onSaved,
}: {
  productId: string;
  canEdit: boolean;
  units: CatalogUnit[];
  onSaved?: () => void;
}) {
  const [detail, setDetail] = useState<ProductCostDetail | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [loadState, setLoadState] = useState<"loading" | "ready" | "error">("loading");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const unitOptions = useMemo(
    () => units.filter((u) => u.active || u.id === form?.rawCostUnitId || u.id === form?.operationalUnitId),
    [units, form?.rawCostUnitId, form?.operationalUnitId],
  );

  const load = useCallback(async () => {
    setLoadState("loading");
    setLoadError(null);
    try {
      const data = await fetchProductCost(productId);
      setDetail(data);
      setForm(toForm(data));
      setLoadState("ready");
    } catch (error) {
      setLoadState("error");
      setLoadError(error instanceof Error ? error.message : "Não foi possível carregar o custo");
    }
  }, [productId]);

  useEffect(() => {
    void load();
  }, [load]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((current) => (current ? { ...current, [key]: value } : current));

  // Loaders do histórico POR COMPONENTE (cada relógio mostra só as alterações do seu componente).
  // Matéria-prima própria: histórico do produto filtrado aos campos não-operacionais.
  const loadRawHistory = useCallback(
    () => fetchProductCostHistory(productId)
      .then((rows) => rows.filter((h) => isRawChange(h.changedFields)).map(toRawView)),
    [productId],
  );
  // Matéria-prima HERDADA: pertence ao produto de ORIGEM (carregado do id da origem).
  const loadOriginRawHistory = useCallback(
    (originId: string) =>
      fetchProductCostHistory(originId)
        .then((rows) => rows.filter((h) => isRawChange(h.changedFields)).map(toRawView)),
    [],
  );
  // Operacional: sempre do próprio produto (não herdado), filtrado aos campos operacionais.
  const loadOperationalHistory = useCallback(
    () => fetchProductCostHistory(productId)
      .then((rows) => rows.filter((h) => isOperationalChange(h.changedFields)).map(toOperationalView)),
    [productId],
  );

  // Seletor de matéria-prima de origem (IN NATURA ativo), por código/nome.
  const [originEditing, setOriginEditing] = useState(false);
  const [candidates, setCandidates] = useState<AdminProductSummary[] | null>(null);
  const [candidateQuery, setCandidateQuery] = useState("");
  const [selectedOrigin, setSelectedOrigin] = useState("");
  const [savingOrigin, setSavingOrigin] = useState(false);
  const [removeArmed, setRemoveArmed] = useState(false);

  const openOriginEditor = () => {
    setOriginEditing(true);
    setRemoveArmed(false);
    setSelectedOrigin(detail?.originProductId ?? "");
    if (candidates == null) {
      fetchAdminProducts()
        .then(setCandidates)
        .catch((e) => toast.error(e instanceof Error ? e.message : "Não foi possível carregar os produtos"));
    }
  };

  const filteredCandidates = useMemo(() => {
    const all = (candidates ?? []).filter(
      (p) => p.active && p.category !== "PROCESSADOS" && p.id !== productId,
    );
    const q = candidateQuery.trim().toLowerCase();
    const list = q
      ? all.filter((p) => p.name.toLowerCase().includes(q) || String(p.code ?? "").includes(q))
      : all;
    return [...list].sort((a, b) => a.name.localeCompare(b.name)).slice(0, 50);
  }, [candidates, candidateQuery, productId]);

  const saveOrigin = async (originId: string | null) => {
    if (!detail) return;
    setSavingOrigin(true);
    try {
      const saved = await updateProductCostOrigin(productId, originId, detail.originProductId);
      setDetail(saved);
      setForm(toForm(saved));
      setOriginEditing(false);
      setRemoveArmed(false);
      toast.success(originId ? "Matéria-prima de origem definida" : "Matéria-prima de origem removida");
      onSaved?.();
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        toast.error("A origem foi alterada por outra pessoa ou está protegida contra edição. Recarregando…");
        void load();
      } else {
        toast.error(error instanceof Error ? error.message : "Não foi possível salvar a origem");
      }
    } finally {
      setSavingOrigin(false);
    }
  };

  const save = async () => {
    if (!form || !detail) return;
    const mode = form.operationalMode === "" ? null : form.operationalMode;
    const inheritsRaw = detail.inheritsRawCost;
    setSaving(true);
    try {
      const saved = await updateProductCost(productId, {
        // Processado herda a matéria-prima da origem: não enviamos campos próprios (o backend os preserva).
        rawCostPrice: inheritsRaw ? null : parseDecimal(form.rawCostPrice),
        rawCostUnitId: inheritsRaw ? null : form.rawCostUnitId || null,
        rawCostReferenceDate: inheritsRaw ? null : form.rawCostReferenceDate || null,
        rawCostNote: inheritsRaw ? null : form.rawCostNote.trim() || null,
        operationalMode: mode,
        operationalValue: mode === "FIXED" ? parseDecimal(form.operationalValue) : null,
        operationalPercent: mode === "PERCENT" ? parseDecimal(form.operationalPercent) : null,
        operationalUnitId: mode === "FIXED" ? form.operationalUnitId || null : null,
        operationalNote: mode ? form.operationalNote.trim() || null : null,
        version: detail.version,
      });
      setDetail(saved);
      setForm(toForm(saved));
      toast.success("Custo salvo");
      onSaved?.();
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        toast.error("O custo foi alterado por outra pessoa. Recarregando…");
        void load();
      } else {
        toast.error(error instanceof Error ? error.message : "Não foi possível salvar o custo");
      }
    } finally {
      setSaving(false);
    }
  };

  if (loadState === "loading") {
    return (
      <div className="rounded-lg border border-border bg-secondary/20 p-6 text-center text-sm text-muted-foreground">
        <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin text-primary" /> Carregando custos…
      </div>
    );
  }
  if (loadState === "error" || !form || !detail) {
    return (
      <div className="rounded-lg border border-border bg-destructive/10 p-4 text-sm text-destructive">
        {loadError ?? "Não foi possível carregar o custo"}
      </div>
    );
  }

  const preview = detail.preview;
  const inactive = !detail.active;
  const inherits = detail.inheritsRawCost;
  const originProductId = detail.originProductId;
  const disabled = !canEdit || saving || inactive;

  return (
    <div className="space-y-4 text-sm">
      {inactive && (
        <div className="rounded-lg border border-border bg-secondary/40 p-3 text-xs text-muted-foreground">
          Produto inativo — custos em <strong>somente leitura</strong> (edição bloqueada no backend).
        </div>
      )}

      {/* Matéria-prima: herdada (processado) ou própria (demais) */}
      {inherits ? (
        <div className="space-y-2 rounded-lg border border-border bg-secondary/30 p-3">
          <div className="flex items-center justify-between">
            <Label className="m-0 font-semibold">Matéria-prima (herdada da origem)</Label>
            <div className="flex items-center gap-1">
              {originProductId && (
                <CostComponentHistory
                  title="Matéria-prima (origem)"
                  subtitle={
                    <>
                      Pertence à matéria-prima de origem <strong>{detail.originProductName}</strong>
                      {detail.originProductCode != null && <span> (Cód. {detail.originProductCode})</span>}.
                    </>
                  }
                  load={() => loadOriginRawHistory(originProductId)}
                  emptyText="Sem alterações registradas na origem."
                />
              )}
              <span className="text-[11px] text-muted-foreground">somente leitura</span>
            </div>
          </div>
          {detail.originProductId ? (
            <>
              <p className="text-xs text-muted-foreground">
                Origem IN NATURA: <strong>{detail.originProductName}</strong>
                {detail.originProductCode != null && <span> (Cód. {detail.originProductCode})</span>}
              </p>
              <dl className="space-y-1">
                <Row label="Custo da matéria-prima (origem)" value={formatBRL(preview.rawCost)} />
                <Row label="Unidade de referência (origem)" value={preview.rawCostUnitName ?? "—"} />
                <Row label="Data de referência (origem)" value={formatDate(detail.inheritedRawCostReferenceDate)} />
              </dl>
              <p className="text-[11px] text-muted-foreground">
                O custo é lido da origem em tempo real; alterar o custo do IN NATURA reflete aqui. A unidade
                exibida é a da origem — não é custo por embalagem/unidade vendida.
              </p>
            </>
          ) : (
            <p className="text-xs text-warning">
              {preview.incompleteReasons.map((r) => INCOMPLETE_REASON_LABELS[r] ?? r).join("; ") ||
                "Matéria-prima de origem não resolvida."}
            </p>
          )}
          {detail.rawCostPrice != null && (
            <p className="text-[11px] text-muted-foreground">
              Custo próprio anterior preservado ({formatBRL(detail.rawCostPrice)}) — <strong>não utilizado</strong>
              {" "}nesta composição.
            </p>
          )}

          {/* Seletor de origem (CATALOGO.CUSTO.EDITAR). Indisponível p/ composição múltipla/parametrizada. */}
          {canEdit && !inactive && !detail.originEditable && (
            <p className="text-[11px] text-muted-foreground">
              Composição com múltiplos componentes ou parâmetros de receita — vínculo <strong>protegido</strong>;
              não editável pelo seletor simples.
            </p>
          )}
          {canEdit && !inactive && detail.originEditable && !originEditing && (
            <Button type="button" variant="outline" size="sm" onClick={openOriginEditor}>
              {detail.originProductId ? "Trocar/remover origem" : "Definir matéria-prima de origem"}
            </Button>
          )}
          {canEdit && !inactive && detail.originEditable && originEditing && (
            <div className="space-y-2 rounded-md border border-border bg-background p-2">
              <Input
                placeholder="Buscar IN NATURA por código ou nome..."
                value={candidateQuery}
                onChange={(e) => setCandidateQuery(e.target.value)}
                disabled={savingOrigin}
              />
              <Select value={selectedOrigin || NONE} onValueChange={(v) => setSelectedOrigin(v === NONE ? "" : v)} disabled={savingOrigin}>
                <SelectTrigger><SelectValue placeholder="Selecione a matéria-prima de origem" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>—</SelectItem>
                  {candidates == null && <SelectItem value="__loading" disabled>Carregando…</SelectItem>}
                  {filteredCandidates.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.code != null ? `${p.code} · ` : ""}{p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex flex-wrap items-center gap-2">
                <Button type="button" variant="hero" size="sm" disabled={savingOrigin || !selectedOrigin}
                  onClick={() => void saveOrigin(selectedOrigin || null)}>
                  {savingOrigin ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Salvar origem
                </Button>
                {detail.originProductId && (
                  removeArmed ? (
                    <Button type="button" variant="ghost" size="sm" className="text-destructive"
                      disabled={savingOrigin} onClick={() => void saveOrigin(null)}>
                      Confirmar remoção
                    </Button>
                  ) : (
                    <Button type="button" variant="ghost" size="sm" disabled={savingOrigin}
                      onClick={() => setRemoveArmed(true)}>
                      Remover origem
                    </Button>
                  )
                )}
                <Button type="button" variant="ghost" size="sm" disabled={savingOrigin}
                  onClick={() => { setOriginEditing(false); setRemoveArmed(false); }}>
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : (
      <div className="space-y-3 rounded-lg border border-border bg-secondary/30 p-3">
        <div className="flex items-center justify-between">
          <Label className="m-0 font-semibold">Custo da matéria-prima</Label>
          <CostComponentHistory title="Custo da matéria-prima" load={loadRawHistory} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs font-normal text-muted-foreground">Preço de custo (R$)</Label>
            <Input
              type="number"
              min={0}
              step="0.0001"
              inputMode="decimal"
              value={form.rawCostPrice}
              onChange={(e) => update("rawCostPrice", e.target.value)}
              disabled={disabled}
              placeholder="Não cadastrado"
              className="tabular-nums"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-normal text-muted-foreground">Unidade de referência</Label>
            <Select
              value={form.rawCostUnitId || NONE}
              onValueChange={(v) => update("rawCostUnitId", v === NONE ? "" : v)}
              disabled={disabled}
            >
              <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>—</SelectItem>
                {unitOptions.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.name}{u.active ? "" : " (inativa)"}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs font-normal text-muted-foreground">Data de referência</Label>
            <Input
              type="date"
              value={form.rawCostReferenceDate}
              onChange={(e) => update("rawCostReferenceDate", e.target.value)}
              disabled={disabled}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-normal text-muted-foreground">Observação</Label>
            <Input
              value={form.rawCostNote}
              onChange={(e) => update("rawCostNote", e.target.value)}
              disabled={disabled}
              placeholder="Opcional"
            />
          </div>
        </div>
      </div>
      )}

      {/* Custo operacional */}
      <div className="space-y-3 rounded-lg border border-border bg-secondary/30 p-3">
        <div className="flex items-center justify-between">
          <Label className="m-0 font-semibold">Custo operacional</Label>
          <CostComponentHistory title="Custo operacional" load={loadOperationalHistory} />
        </div>
        <p className="text-xs text-muted-foreground">
          Modalidades alternativas (não somadas): valor fixo por unidade OU percentual sobre a matéria-prima
          (antes da merma).
        </p>
        <div className="space-y-1">
          <Label className="text-xs font-normal text-muted-foreground">Modalidade</Label>
          <Select
            value={form.operationalMode || NONE}
            onValueChange={(v) => update("operationalMode", v === NONE ? "" : (v as OperationalCostMode))}
            disabled={disabled}
          >
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Não cadastrado</SelectItem>
              <SelectItem value="FIXED">Valor fixo (R$ por unidade)</SelectItem>
              <SelectItem value="PERCENT">Percentual sobre a matéria-prima</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {form.operationalMode === "FIXED" && (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs font-normal text-muted-foreground">Valor (R$)</Label>
              <Input
                type="number"
                min={0}
                step="0.0001"
                inputMode="decimal"
                value={form.operationalValue}
                onChange={(e) => update("operationalValue", e.target.value)}
                disabled={disabled}
                className="tabular-nums"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-normal text-muted-foreground">Unidade aplicável</Label>
              <Select
                value={form.operationalUnitId || NONE}
                onValueChange={(v) => update("operationalUnitId", v === NONE ? "" : v)}
                disabled={disabled}
              >
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>—</SelectItem>
                  {unitOptions.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name}{u.active ? "" : " (inativa)"}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
        {form.operationalMode === "PERCENT" && (
          <div className="space-y-1">
            <Label className="text-xs font-normal text-muted-foreground">Percentual (%)</Label>
            <Input
              type="number"
              min={0}
              step="0.01"
              inputMode="decimal"
              value={form.operationalPercent}
              onChange={(e) => update("operationalPercent", e.target.value)}
              disabled={disabled}
              className="tabular-nums"
            />
          </div>
        )}
        {form.operationalMode !== "" && (
          <div className="space-y-1">
            <Label className="text-xs font-normal text-muted-foreground">Observação</Label>
            <Input
              value={form.operationalNote}
              onChange={(e) => update("operationalNote", e.target.value)}
              disabled={disabled}
              placeholder="Opcional"
            />
          </div>
        )}
      </div>

      {/* Custo base da Etapa 1 (matéria-prima + merma + operacional) na unidade de referência. */}
      <div className="space-y-2 rounded-lg border border-primary/30 bg-primary/5 p-3">
        <div className="flex items-center justify-between">
          <Label className="m-0 font-semibold">Custo base</Label>
          <span className="text-[11px] text-muted-foreground">reflete os valores salvos</span>
        </div>
        <dl className="space-y-1">
          <Row label="Matéria-prima" value={formatBRL(preview.rawCost)} />
          <Row
            label={`Acréscimo por merma (${formatPercent(preview.mermaPercentApplied)}${preview.mermaDefault ? ", padrão" : ""})`}
            value={formatBRL(preview.mermaSurcharge)}
          />
          <Row
            label={`Custo operacional${preview.operationalMode ? ` (${preview.operationalMode === "FIXED" ? "fixo" : "percentual"})` : ""}`}
            value={formatBRL(preview.operationalCost)}
          />
        </dl>
        {preview.mermaDefault && preview.mermaComment && (
          <p className="text-xs font-medium text-warning">{preview.mermaComment}</p>
        )}
        {preview.complete ? (
          <div className="flex items-center justify-between border-t border-primary/20 pt-2 text-base font-semibold">
            <span>Custo base</span>
            <span className="tabular-nums">{formatBRL(preview.partialCost)}</span>
          </div>
        ) : (
          <div className="border-t border-primary/20 pt-2 text-xs text-muted-foreground">
            <p className="font-medium text-foreground">Custo base incompleto — total não exibido.</p>
            <ul className="mt-1 list-inside list-disc">
              {preview.incompleteReasons.map((r) => (
                <li key={r}>{INCOMPLETE_REASON_LABELS[r] ?? r}</li>
              ))}
            </ul>
          </div>
        )}
        <p className="text-[11px] text-muted-foreground">Matéria-prima + merma + operacional (sem embalagens e despesas da negociação).</p>
        <p className="text-[11px] text-muted-foreground">
          A merma é definida no cadastro do produto e <strong>não possui histórico próprio</strong> — por isso
          não há relógio nesta linha.
        </p>
      </div>

      <PackagingSection
        productId={productId}
        presentations={detail.packagingByPresentation}
        canEdit={canEdit && !inactive}
        onDetailChange={(d) => { setDetail(d); setForm(toForm(d)); }}
      />

      {canEdit && !inactive && (
        <div className="flex justify-end">
          <Button type="button" variant="hero" onClick={() => void save()} disabled={disabled}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Salvar custo
          </Button>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="tabular-nums">{value}</dd>
    </div>
  );
}

const STATE_LABEL: Record<PackagingCompositionState, string> = {
  NAO_CADASTRADA: "Não cadastrada",
  SEM_EMBALAGEM: "Sem embalagem",
  COM_EMBALAGENS: "Com embalagens",
};

type DraftRow = { materialId: string; quantity: string };

function PackagingSection({
  productId,
  presentations,
  canEdit,
  onDetailChange,
}: {
  productId: string;
  presentations: PackagingPresentation[];
  canEdit: boolean;
  onDetailChange: (d: ProductCostDetail) => void;
}) {
  const [editingUnit, setEditingUnit] = useState<string | null>(null);
  const [draftState, setDraftState] = useState<"SEM_EMBALAGEM" | "COM_EMBALAGENS">("SEM_EMBALAGEM");
  const [rows, setRows] = useState<DraftRow[]>([]);
  const [options, setOptions] = useState<PackagingMaterialOption[] | null>(null);
  const [savingUnit, setSavingUnit] = useState(false);
  const [clearArmUnit, setClearArmUnit] = useState<string | null>(null);

  const clearComposition = async (p: PackagingPresentation) => {
    setSavingUnit(true);
    try {
      const saved = await clearProductPackaging(productId, p.unitId, p.version);
      toast.success("Composição limpa (voltou a Não cadastrada)");
      setClearArmUnit(null);
      onDetailChange(saved);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        toast.error("A composição foi alterada por outra pessoa. Recarregando…");
        try { onDetailChange(await fetchProductCost(productId)); } catch { /* ignore */ }
        setClearArmUnit(null);
      } else {
        toast.error(e instanceof Error ? e.message : "Não foi possível limpar a composição");
      }
    } finally {
      setSavingUnit(false);
    }
  };

  const openEditor = (p: PackagingPresentation) => {
    setEditingUnit(p.unitId);
    setDraftState(p.state === "COM_EMBALAGENS" ? "COM_EMBALAGENS" : "SEM_EMBALAGEM");
    setRows(p.components.map((c) => ({ materialId: c.materialId, quantity: String(c.quantity) })));
    if (options == null) {
      fetchPackagingMaterialOptions()
        .then(setOptions)
        .catch((e) => toast.error(e instanceof Error ? e.message : "Não foi possível carregar os materiais"));
    }
  };

  const save = async (p: PackagingPresentation) => {
    setSavingUnit(true);
    try {
      const components = draftState === "COM_EMBALAGENS"
        ? rows
            .filter((r) => r.materialId)
            .map((r) => ({ materialId: r.materialId, quantity: Number((r.quantity || "0").replace(",", ".")) }))
        : [];
      const saved = await upsertProductPackaging(productId, p.unitId, {
        state: draftState,
        components,
        version: p.version,
      });
      toast.success("Composição salva");
      setEditingUnit(null);
      onDetailChange(saved);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        toast.error("A composição foi alterada por outra pessoa. Recarregando…");
        try {
          onDetailChange(await fetchProductCost(productId));
        } catch { /* ignore */ }
        setEditingUnit(null);
      } else {
        toast.error(e instanceof Error ? e.message : "Não foi possível salvar a composição");
      }
    } finally {
      setSavingUnit(false);
    }
  };

  if (presentations.length === 0) return null;

  return (
    <div className="space-y-2 rounded-lg border border-border bg-secondary/30 p-3">
      <Label className="m-0 font-semibold">Embalagens por apresentação</Label>
      <p className="text-[11px] text-muted-foreground">
        A composição vale para todos os cortes da apresentação. Custo parcial soma embalagem ao custo base
        apenas quando a apresentação coincide com a unidade de referência (sem conversão automática).
      </p>
      {presentations.map((p) => {
        const editing = editingUnit === p.unitId;
        return (
          <div key={p.unitId} className="rounded-md border border-border bg-background p-2 text-xs">
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium">{p.unitName}</span>
              <div className="flex items-center gap-1">
                <CostComponentHistory
                  title={`Composição de embalagem — ${p.unitName}`}
                  load={() => fetchProductPackagingHistory(productId, p.unitId).then((r) => r.map(toCompositionView))}
                  emptyText="Sem alterações de composição registradas."
                />
                <span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                  {STATE_LABEL[p.state]}
                </span>
              </div>
            </div>

            {!editing && (
              <>
                {p.state === "COM_EMBALAGENS" && (
                  <ul className="mt-1 space-y-0.5">
                    {p.components.map((c) => (
                      <li key={c.materialId} className="flex items-center justify-between gap-2">
                        <span className={cn(!c.materialActive && "text-warning")}>
                          {c.materialName} · {c.quantity} {c.materialUnitLabel ?? ""}
                          {!c.materialActive && " (inativo)"}
                          {!c.materialHasCost && " (sem custo)"}
                        </span>
                        <div className="flex items-center gap-1">
                          <span className="tabular-nums text-muted-foreground">{formatBRL(c.lineCost)}</span>
                          <CostComponentHistory
                            title={`Custo do material — ${c.materialName}`}
                            load={() => fetchPackagingMaterialHistory(c.materialId).then((r) => r.map(toMaterialView))}
                            emptyText="Sem alterações de custo registradas para este material."
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="mt-1 flex items-center justify-between">
                  <span className="text-muted-foreground">Custo das embalagens</span>
                  <span className="tabular-nums">{formatBRL(p.packagingCost)}</span>
                </div>
                {!p.packagingComplete && p.packagingReasons.length > 0 && (
                  <p className="mt-1 text-warning">
                    {p.packagingReasons.map((r) => PACKAGING_REASON_LABELS[r] ?? r).join("; ")}
                  </p>
                )}
                <div className="mt-1 flex items-center justify-between border-t border-border pt-1 font-semibold">
                  <span>Custo parcial (com embalagem)</span>
                  <span className="tabular-nums">
                    {p.partialComplete ? formatBRL(p.partialWithPackaging) : "—"}
                  </span>
                </div>
                {!p.partialComplete && (
                  <p className="mt-0.5 text-[11px] text-muted-foreground">
                    {p.partialReasons.map((r) => PACKAGING_REASON_LABELS[r] ?? r).join("; ")}
                  </p>
                )}
                {canEdit && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => openEditor(p)}>
                      <Pencil className="h-3.5 w-3.5" /> Editar composição
                    </Button>
                    {p.state !== "NAO_CADASTRADA" && (
                      clearArmUnit === p.unitId ? (
                        <Button type="button" variant="ghost" size="sm" className="text-destructive"
                          disabled={savingUnit} onClick={() => void clearComposition(p)}>
                          Confirmar: voltar a Não cadastrada
                        </Button>
                      ) : (
                        <Button type="button" variant="ghost" size="sm" disabled={savingUnit}
                          onClick={() => setClearArmUnit(p.unitId)}>
                          Limpar composição
                        </Button>
                      )
                    )}
                  </div>
                )}
              </>
            )}

            {editing && (
              <div className="mt-2 space-y-2">
                <Select value={draftState} onValueChange={(v) => setDraftState(v as "SEM_EMBALAGEM" | "COM_EMBALAGENS")} disabled={savingUnit}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SEM_EMBALAGEM">Sem embalagem (custo 0)</SelectItem>
                    <SelectItem value="COM_EMBALAGENS">Com embalagens</SelectItem>
                  </SelectContent>
                </Select>
                {draftState === "COM_EMBALAGENS" && (
                  <div className="space-y-2">
                    {rows.map((r, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Select value={r.materialId || NONE}
                          onValueChange={(v) => setRows((rs) => rs.map((x, j) => j === i ? { ...x, materialId: v === NONE ? "" : v } : x))}
                          disabled={savingUnit}>
                          <SelectTrigger className="flex-1"><SelectValue placeholder="Material" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NONE}>—</SelectItem>
                            {(options ?? []).map((o) => (
                              <SelectItem key={o.id} value={o.id}>{o.name} ({o.materialUnitLabel})</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input type="number" min={0} step="0.0001" inputMode="decimal" className="w-24 tabular-nums"
                          placeholder="Qtd" value={r.quantity}
                          onChange={(e) => setRows((rs) => rs.map((x, j) => j === i ? { ...x, quantity: e.target.value } : x))}
                          disabled={savingUnit} />
                        <Button type="button" variant="ghost" size="sm" onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))} disabled={savingUnit}>✕</Button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm"
                      onClick={() => setRows((rs) => [...rs, { materialId: "", quantity: "" }])} disabled={savingUnit}>
                      <Plus className="h-3.5 w-3.5" /> Adicionar material
                    </Button>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button type="button" variant="hero" size="sm" onClick={() => void save(p)} disabled={savingUnit}>
                    {savingUnit ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salvar
                  </Button>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setEditingUnit(null)} disabled={savingUnit}>Cancelar</Button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
